import { iterateAttachmentParts } from './message-attachments';
import type { Chat, Settings, ChatGroup, MessageNode, ChatMeta, ChatContent, SidebarItem, StorageSnapshot, BinaryObject } from '@/01-models/types';
import type { AttachmentId, BinaryObjectId, ChatGroupId, ChatId, VolumeId } from '@/01-models/ids';
import {
  type ChatMetaDto,
  type ChatGroupDto,
  type HierarchyDto,
  ChatMetaSchemaDto,
  ChatGroupSchemaDto,
  SettingsSchemaDto,
  HierarchySchemaDto,
  ChatContentSchemaDto,
} from '@/00-storage/00-dto/dto';
import {
  chatToDomain,
  chatToDto,
  chatGroupToDomain,
  chatGroupConfigurationToDomain,
  chatGroupToDto,
  settingsToDomain,
  settingsToDto,
  hierarchyToDomain,
  hierarchyToDto,
  chatMetaToDto,
  chatMetaToDomain,
  chatContentToDto,
  chatContentToDomain,
  buildSidebarItemsFromHierarchy,
} from '@/00-storage/mapper/mappers';
import { IStorageProvider } from './interface';

import { STORAGE_KEY_PREFIX } from '@/constants';
import { idToRaw, toChatGroupId, toChatId } from '@/01-models/ids';
import { promiseAllKeyed } from '@/utils/promise';
import { ExperimentalNaidanRpcRegistrySchemaDto, type ExperimentalNaidanRpcRegistryDto } from '@/00-storage/00-dto/experimental-naidan-rpc.dto';

const LSP_STORAGE_PREFIX = `${STORAGE_KEY_PREFIX}lsp:`;
const KEY_HIERARCHY = `${LSP_STORAGE_PREFIX}hierarchy`;
const KEY_SETTINGS = `${LSP_STORAGE_PREFIX}settings`;
const KEY_NAIDAN_RPC_REGISTRY = `${LSP_STORAGE_PREFIX}experimental-naidan-rpc-connections`;
const KEY_META_PREFIX = `${LSP_STORAGE_PREFIX}chat_meta:`;
const KEY_GROUP_PREFIX = `${LSP_STORAGE_PREFIX}chat_group:`;
const KEY_CONTENT_PREFIX = `${LSP_STORAGE_PREFIX}chat_content:`;

/**
 * LocalStorage Implementation
 * Optimized by splitting metadata and content into individual keys.
 * Uses a centralized hierarchy record for ordering.
 */
export class LocalStorageProvider extends IStorageProvider {
  readonly canPersistBinary = false;
  private blobCache = new Map<AttachmentId, Blob>();

  async loadNaidanRpcRegistry(): Promise<ExperimentalNaidanRpcRegistryDto | undefined> {
    const raw = localStorage.getItem(KEY_NAIDAN_RPC_REGISTRY);
    return raw === null ? undefined : ExperimentalNaidanRpcRegistrySchemaDto.parse(JSON.parse(raw));
  }

  async saveNaidanRpcRegistry({ registry }: { registry: ExperimentalNaidanRpcRegistryDto | undefined }): Promise<void> {
    if (registry === undefined) {
      localStorage.removeItem(KEY_NAIDAN_RPC_REGISTRY);
    } else {
      localStorage.setItem(KEY_NAIDAN_RPC_REGISTRY, JSON.stringify(ExperimentalNaidanRpcRegistrySchemaDto.parse(registry)));
    }
  }

  private restoreBlobs({ nodes }: { nodes: MessageNode[] }): void {
    for (const part of iterateAttachmentParts({ nodes })) {
      const att = part.attachment;
      switch (att.status) {
      case 'memory': {
        const cached = this.blobCache.get(att.id);
        if (cached) {
          part.attachment = { ...att, blob: cached };
        }
        break;
      }
      case 'persisted':
      case 'missing':
        break;
      default: {
        const _ex: never = att;
        throw new Error(`Unhandled attachment status: ${_ex}`);
      }
      }
    }
  }

  private loadUnhydratedChatContent({ id }: { id: ChatId }): ChatContent | null {
    const rawContent = localStorage.getItem(`${KEY_CONTENT_PREFIX}${idToRaw({ id })}`);
    if (rawContent === null) {
      return null;
    }

    // A stored but unreadable record must not become a new, empty conversation
    // when a caller performs a read-update-write operation.
    return chatContentToDomain({
      dto: ChatContentSchemaDto.parse(JSON.parse(rawContent)),
    });
  }

  async init(): Promise<void> {
    // No-op for localStorage
  }

  // --- Internal Data Access ---

  protected async listChatMetasRaw(): Promise<ChatMetaDto[]> {
    const metas: ChatMetaDto[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(KEY_META_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (raw !== null) {
          metas.push(ChatMetaSchemaDto.parse(JSON.parse(raw)));
        }
      }
    }
    return metas;
  }

  protected async listChatGroupsRaw(): Promise<ChatGroupDto[]> {
    const groups: ChatGroupDto[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(KEY_GROUP_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (raw !== null) {
          groups.push(ChatGroupSchemaDto.parse(JSON.parse(raw)));
        }
      }
    }
    return groups;
  }

  // --- Hierarchy Management ---

  async loadHierarchy(): Promise<HierarchyDto | null> {
    const raw = localStorage.getItem(KEY_HIERARCHY);
    if (raw === null) {
      return { items: [] };
    }
    return HierarchySchemaDto.parse(JSON.parse(raw));
  }

  async saveHierarchy({ hierarchy }: { hierarchy: HierarchyDto }): Promise<void> {
    localStorage.setItem(KEY_HIERARCHY, JSON.stringify(hierarchy));
  }

  // --- Persistence Implementation ---

  async saveChatMeta({ meta }: { meta: ChatMeta }): Promise<void> {
    const dto = chatMetaToDto({ domain: meta });
    const serialized = JSON.stringify(ChatMetaSchemaDto.parse(dto));
    localStorage.setItem(`${KEY_META_PREFIX}${idToRaw({ id: meta.id })}`, serialized);
  }

  async saveChatContent({ id, content }: { id: ChatId, content: ChatContent }): Promise<void> {
    for (const part of iterateAttachmentParts({ nodes: content.root.items })) {
      const att = part.attachment;
      switch (att.status) {
      case 'memory':
        if (att.blob) {
          this.blobCache.set(att.id, att.blob);
        }
        break;
      case 'persisted':
      case 'missing':
        break;
      default: {
        const _ex: never = att;
        throw new Error(`Unhandled attachment status: ${_ex}`);
      }
      }
    }

    const dto = chatContentToDto({ domain: content });
    ChatContentSchemaDto.parse(dto);
    localStorage.setItem(`${KEY_CONTENT_PREFIX}${idToRaw({ id })}`, JSON.stringify(dto));
  }

  async loadChat({ id }: { id: ChatId }): Promise<Chat | null> {
    const rawMeta = localStorage.getItem(`${KEY_META_PREFIX}${idToRaw({ id })}`);
    const rawContent = localStorage.getItem(`${KEY_CONTENT_PREFIX}${idToRaw({ id })}`);
    const meta = rawMeta === null ? null : ChatMetaSchemaDto.parse(JSON.parse(rawMeta));
    const content = rawContent === null ? null : ChatContentSchemaDto.parse(JSON.parse(rawContent));
    if (meta === null || content === null) {
      return null;
    }
    const chat = chatToDomain({ dto: { ...meta, ...content, experimental: meta.experimental, messages: undefined } });

    // Resolve groupId from hierarchy
    const hierarchy = await this.loadHierarchy();
    if (hierarchy) {
      const group = hierarchy.items.find(i => i.type === 'chat_group' && i.chat_ids.includes(idToRaw({ id })));
      if (group) {
        chat.groupId = toChatGroupId({ raw: group.id });
      }
    }

    this.restoreBlobs({ nodes: chat.root.items });

    return chat;
  }

  async loadChatMeta({ id }: { id: ChatId }): Promise<ChatMeta | null> {
    const rawMeta = localStorage.getItem(`${KEY_META_PREFIX}${idToRaw({ id })}`);
    if (rawMeta === null) {
      return null;
    }
    const meta = chatMetaToDomain({ dto: ChatMetaSchemaDto.parse(JSON.parse(rawMeta)) });
    // Resolve groupId from hierarchy
    const hierarchy = await this.loadHierarchy();
    if (hierarchy) {
      const group = hierarchy.items.find(i => i.type === 'chat_group' && i.chat_ids.includes(idToRaw({ id })));
      if (group) {
        meta.groupId = toChatGroupId({ raw: group.id });
      }
    }
    return meta;
  }

  async loadChatContent({ id }: { id: ChatId }): Promise<ChatContent | null> {
    const content = this.loadUnhydratedChatContent({ id });
    if (content === null) {
      return null;
    }

    this.restoreBlobs({ nodes: content.root.items });
    return content;
  }

  async loadChatContentWithoutAttachments({ id }: { id: ChatId }): Promise<ChatContent | null> {
    return this.loadUnhydratedChatContent({ id });
  }

  async deleteChat({ id }: { id: ChatId }): Promise<void> {
    localStorage.removeItem(`${KEY_META_PREFIX}${idToRaw({ id })}`);
    localStorage.removeItem(`${KEY_CONTENT_PREFIX}${idToRaw({ id })}`);
  }

  async saveChatGroup({ chatGroup }: { chatGroup: ChatGroup }): Promise<void> {
    const dto = chatGroupToDto({ domain: chatGroup });
    const serialized = JSON.stringify(ChatGroupSchemaDto.parse(dto));
    localStorage.setItem(`${KEY_GROUP_PREFIX}${idToRaw({ id: chatGroup.id })}`, serialized);
  }

  async loadChatGroupConfiguration({ id }: { id: ChatGroupId }): Promise<Omit<ChatGroup, 'items'> | null> {
    const raw = localStorage.getItem(`${KEY_GROUP_PREFIX}${idToRaw({ id })}`);
    return raw === null ? null : chatGroupConfigurationToDomain({ dto: ChatGroupSchemaDto.parse(JSON.parse(raw)) });
  }

  async loadChatGroup({ id }: { id: ChatGroupId }): Promise<ChatGroup | null> {
    const raw = localStorage.getItem(`${KEY_GROUP_PREFIX}${idToRaw({ id })}`);
    if (raw === null) {
      return null;
    }
    const { hierarchy, allMetas } = await promiseAllKeyed({
      hierarchy: this.loadHierarchy(),
      allMetas: this.listChatMetasRaw(),
    });
    const chatMetas = allMetas.map(dto => chatMetaToDomain({ dto }));
    const h = hierarchyToDomain({ dto: hierarchy || { items: [] } });
    return chatGroupToDomain({ dto: ChatGroupSchemaDto.parse(JSON.parse(raw)), hierarchy: h, chatMetas });
  }

  async deleteChatGroup({ id }: { id: ChatGroupId }): Promise<void> {
    localStorage.removeItem(`${KEY_GROUP_PREFIX}${idToRaw({ id })}`);
  }

  public override async getSidebarStructure(): Promise<SidebarItem[]> {
    const { rawHierarchy, rawMetas, rawGroups } = await promiseAllKeyed({
      rawHierarchy: this.loadHierarchy(),
      rawMetas: this.listChatMetasRaw(),
      rawGroups: this.listChatGroupsRaw(),
    });

    const hierarchy = hierarchyToDomain({ dto: rawHierarchy || { items: [] } });
    const chatMetas = rawMetas.map(dto => chatMetaToDomain({ dto }));
    const chatGroups = rawGroups.map(dto => chatGroupToDomain({ dto, hierarchy, chatMetas }));

    return buildSidebarItemsFromHierarchy({ hierarchy, chatMetas, chatGroups });
  }

  async saveSettings({ settings }: { settings: Settings }): Promise<void> {
    const dto = settingsToDto({ domain: settings });
    localStorage.setItem(KEY_SETTINGS, JSON.stringify(SettingsSchemaDto.parse(dto)));
  }

  async loadSettings(): Promise<Settings | null> {
    const raw = localStorage.getItem(KEY_SETTINGS);
    if (!raw) {
      return null;
    }
    try {
      return settingsToDomain({ dto: SettingsSchemaDto.parse(JSON.parse(raw)) });
    } catch (error) {
      // Unavailable RPC bytes must not masquerade as absent settings.
      return null;
    }
  }

  // --- Volume Management ---

  async *listVolumes(): AsyncIterable<import('@/01-models/types').Volume> {
    // LocalStorage doesn't support volumes
  }

  async createVolume({ name: _name, type: _type, sourceHandle: _sourceHandle }: {
    name: string,
    type: import('@/01-models/types').VolumeType,
    sourceHandle: FileSystemDirectoryHandle,
  }): Promise<import('@/01-models/types').Volume> {
    throw new Error('Volume management is not supported in LocalStorage provider.');
  }

  async createVolumeFromFiles({ name: _name, entries: _entries, onProgress: _onProgress, signal: _signal }: {
    name: string,
    entries: Array<{ file: File, relativePath: string }>,
    onProgress?: ({ processed, total }: { processed: number, total: number }) => void,
    signal?: AbortSignal,
  }): Promise<import('@/01-models/types').Volume> {
    throw new Error('Volume management is not supported in LocalStorage provider.');
  }

  async getVolumeDirectoryHandle({ volumeId: _volumeId }: {
    volumeId: VolumeId,
  }): Promise<FileSystemDirectoryHandle | null> {
    return null;
  }

  async deleteVolume({ volumeId: _volumeId }: {
    volumeId: VolumeId,
  }): Promise<void> {
    throw new Error('Volume management is not supported in LocalStorage provider.');
  }

  async renameVolume({ volumeId: _volumeId, name: _name }: {
    volumeId: VolumeId,
    name: string,
  }): Promise<void> {
    throw new Error('Volume management is not supported in LocalStorage provider.');
  }

  // --- File Storage ---

  async saveFile({
    blob: _blob,
    binaryObjectId: _binaryObjectId,
    name: _name,
    mimeType: _mimeType,
  }: {
    blob: Blob,
    binaryObjectId: BinaryObjectId,
    name: string,
    mimeType?: string,
  }): Promise<void> {
    throw new Error('File persistence is not supported in LocalStorage provider.');
  }

  async getFile({ binaryObjectId: _binaryObjectId }: { binaryObjectId: BinaryObjectId }): Promise<Blob | null> {
    return null;
  }

  async getBinaryObject({ binaryObjectId: _id }: { binaryObjectId: BinaryObjectId }): Promise<BinaryObject | null> {
    return null;
  }

  async hasAttachments(): Promise<boolean> {
    return false;
  }

  async *listBinaryObjects(): AsyncIterable<BinaryObject> {
    // Yields nothing
  }

  async deleteBinaryObject({ binaryObjectId: _binaryObjectId }: { binaryObjectId: BinaryObjectId }): Promise<void> {
    // No-op
  }

  async clearAll(): Promise<void> {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(LSP_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    this.blobCache.clear();
  }

  // --- Migration Implementation ---

  async dump(): Promise<StorageSnapshot> {
    const { settings, hierarchy, rawMetas, rawGroups } = await promiseAllKeyed({
      settings: this.loadSettings(),
      hierarchy: this.loadHierarchy(),
      rawMetas: this.listChatMetasRaw(),
      rawGroups: this.listChatGroupsRaw(),
    });

    const chatMetas = rawMetas.map(dto => chatMetaToDomain({ dto }));
    const h = hierarchyToDomain({ dto: hierarchy || { items: [] } });
    const chatGroups = rawGroups.map(dto => chatGroupToDomain({ dto, hierarchy: h, chatMetas }));

    const contentStream = async function* (this: LocalStorageProvider) {
      for (const m of rawMetas) {
        const chat = await this.loadChat({ id: toChatId({ raw: m.id }) });
        if (chat) {
          yield { type: 'chat' as const, data: chatToDto({ domain: chat }) };
        }
      }
    };

    return {
      structure: {
        settings: settings || {
          titleGeneration: { endpoint: 'same_scope', model: 'same_scope', lmParameters: { temperature: undefined, topP: undefined, maxCompletionTokens: undefined, presencePenalty: undefined, frequencyPenalty: undefined, stop: undefined, reasoning: { effort: undefined } } },
          providerProfiles: [],
          mounts: [],
          storageType: 'local',
          endpoint: { type: 'openai', url: '' },
        } satisfies Settings,
        hierarchy: h,
        chatMetas,
        chatGroups,
      },
      contentStream: contentStream.call(this),
    };
  }

  async restore({ snapshot }: { snapshot: StorageSnapshot }): Promise<void> {
    const { structure, contentStream } = snapshot;

    // 1. Restore Structural Metadata (skeleton)
    if (structure.settings) {
      await this.saveSettings({ settings: structure.settings });
    }
    if (structure.hierarchy) {
      await this.saveHierarchy({ hierarchy: hierarchyToDto({ domain: structure.hierarchy }) });
    }
    if (structure.chatMetas) {
      for (const meta of structure.chatMetas) {
        await this.saveChatMeta({ meta });
      }
    }
    if (structure.chatGroups) {
      for (const group of structure.chatGroups) {
        await this.saveChatGroup({ chatGroup: group });
      }
    }

    // 2. Restore Heavy Content (trees)
    for await (const chunk of contentStream) {
      const type = chunk.type;
      switch (type) {
      case 'chat': {
        const domainChat = chatToDomain({ dto: chunk.data });
        await this.saveChatContent({ id: domainChat.id, content: domainChat });
        // Ensure meta is consistent with content
        await this.saveChatMeta({ meta: domainChat });
        break;
      }
      case 'binary_object':
        // LocalStorage does not support binary attachments, skip
        break;
      default: {
        const _ex: never = type;
        throw new Error(`Unknown chunk type: ${_ex}`);
      }
      }
    }
  }
}

// Export internal state and logic used only for testing here. Do not reference these in production logic.
// ESLint-required for TypeScript modules.
export const TEST_ONLY = {
};
